/**
 * Manuscript header and tab strip for the shared reader.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useCallback, useEffect, useRef } from "react";
import type { Manuscript, ManuscriptViewerTheme } from "./types.js";

interface ManuscriptHeaderProps {
  readonly manuscript: Manuscript;
  readonly manuscripts: readonly Manuscript[];
  readonly showTabs: boolean;
  readonly totalWords: number;
  readonly theme: ManuscriptViewerTheme;
  readonly tabIdFor: (id: string) => string;
  readonly panelId: string;
  readonly onSwitchManuscript: (id: string) => void;
}

export function ManuscriptHeader({
  manuscript,
  manuscripts,
  showTabs,
  totalWords,
  theme,
  tabIdFor,
  panelId,
  onSwitchManuscript,
}: ManuscriptHeaderProps): React.JSX.Element {
  return (
    <header
      style={{
        background: `linear-gradient(135deg, ${theme.surfaceMuted}, ${theme.surface})`,
        borderBottom: `1px solid ${theme.surfaceMuted}`,
        padding: "30px 32px 24px",
      }}
    >
      {manuscript.eyebrow !== undefined ? (
        <span
          style={{
            color: theme.accentSoft,
            fontFamily: theme.serif,
            fontSize: 12,
            fontWeight: 650,
            letterSpacing: 0,
            textTransform: "uppercase",
          }}
        >
          {manuscript.eyebrow}
        </span>
      ) : null}
      <h1
        style={{
          color: theme.bright,
          fontFamily: theme.serif,
          fontSize: 28,
          fontWeight: 520,
          letterSpacing: 0,
          lineHeight: 1.12,
          margin: "5px 0 0",
        }}
      >
        {manuscript.label}
      </h1>
      <p
        style={{
          color: theme.muted,
          fontFamily: theme.serif,
          fontSize: 14,
          lineHeight: 1.45,
          margin: "8px 0 0",
        }}
      >
        {manuscript.tagline ??
          `${manuscript.chapters.length} chapters · ${(manuscript.appendices?.length ?? 0).toString()} appendices${
            totalWords > 0 ? ` · ${totalWords.toLocaleString()} words` : ""
          }`}
      </p>
      {showTabs ? (
        <ManuscriptTabStrip
          manuscripts={manuscripts}
          activeId={manuscript.id}
          theme={theme}
          tabIdFor={tabIdFor}
          panelId={panelId}
          onSwitchManuscript={onSwitchManuscript}
        />
      ) : null}
    </header>
  );
}

interface ManuscriptTabStripProps {
  readonly manuscripts: readonly Manuscript[];
  readonly activeId: string;
  readonly theme: ManuscriptViewerTheme;
  readonly tabIdFor: (id: string) => string;
  readonly panelId: string;
  readonly onSwitchManuscript: (id: string) => void;
}

function ManuscriptTabStrip({
  manuscripts,
  activeId,
  theme,
  tabIdFor,
  panelId,
  onSwitchManuscript,
}: ManuscriptTabStripProps): React.JSX.Element {
  const tabRefs = useRef<Map<string, HTMLButtonElement | null>>(new Map());
  const focusAfterSwitch = useRef(false);

  const select = useCallback(
    (id: string): void => {
      onSwitchManuscript(id);
    },
    [onSwitchManuscript],
  );

  useEffect(() => {
    if (!focusAfterSwitch.current) return;
    focusAfterSwitch.current = false;
    tabRefs.current.get(activeId)?.focus();
  }, [activeId]);

  const onTabKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>, id: string): void => {
      const index = manuscripts.findIndex((m) => m.id === id);
      if (index < 0) return;
      let next: string | undefined;
      switch (event.key) {
        case "ArrowRight":
        case "ArrowDown":
          next = manuscripts[(index + 1) % manuscripts.length]?.id;
          break;
        case "ArrowLeft":
        case "ArrowUp":
          next = manuscripts[(index - 1 + manuscripts.length) % manuscripts.length]?.id;
          break;
        case "Home":
          next = manuscripts[0]?.id;
          break;
        case "End":
          next = manuscripts[manuscripts.length - 1]?.id;
          break;
        default:
          return;
      }
      if (next === undefined) return;
      event.preventDefault();
      focusAfterSwitch.current = true;
      select(next);
    },
    [manuscripts, select],
  );

  return (
    <div
      role="tablist"
      aria-label="Manuscript selector"
      style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 18 }}
    >
      {manuscripts.map((manuscript) => {
        const active = manuscript.id === activeId;
        return (
          <button
            key={manuscript.id}
            ref={(node) => {
              if (node === null) tabRefs.current.delete(manuscript.id);
              else tabRefs.current.set(manuscript.id, node);
            }}
            type="button"
            role="tab"
            id={tabIdFor(manuscript.id)}
            aria-selected={active}
            aria-controls={panelId}
            tabIndex={active ? 0 : -1}
            onClick={() => {
              select(manuscript.id);
            }}
            onKeyDown={(event) => {
              onTabKeyDown(event, manuscript.id);
            }}
            style={{
              background: active ? theme.accent : theme.surface,
              border: `1px solid ${active ? theme.accent : theme.border}`,
              borderRadius: 8,
              color: active ? theme.void : theme.text,
              cursor: "pointer",
              fontFamily: theme.serif,
              fontSize: 12,
              fontWeight: active ? 700 : 520,
              letterSpacing: 0,
              padding: "6px 13px",
            }}
          >
            {manuscript.label}
          </button>
        );
      })}
    </div>
  );
}
